// A patient's week 1, built from a clinic template (server/plans/weekOne.ts) and done in the exercise lab
// (src/plan/WeekPage.tsx). Every door that proposes exercises ends here: the pain map, the body scan and the
// chat (founder, 2026-10-19, docs/decisions.md).
import type { ProgramExercise, Words } from "./programs";

export type WeekGroup = "warmup" | "main" | "extra";

export interface WeekItem {
  group: WeekGroup;
  /** How many rounds of the exercise's reps, hold or minutes. */
  sets: number;
  exercise: ProgramExercise;
  /** Done twice that day (Dr. Remi sets once or twice a day, plan constitution day_groups). */
  twice?: boolean;
}

export interface WeekDay {
  /** 1 to 7. */
  day: number;
  /** Day 7, which rests the exercises (plan constitution v2, schedule_week; it may still hold walking), and any day with nothing to do. */
  rest: boolean;
  items: WeekItem[];
}

/**
 * One later week, shown as a locked outline (founder, 2026-10-18). It never carries the week's exercises: week 2
 * opens only with the subscription, the full consultation and a passed week-1 check-in (entry_full_plan).
 */
export interface LaterWeek {
  week: number;
  phase: Words | null;
  exercises: number;
}

export interface WeekOne {
  templateKey: string;
  version: number;
  area: string;
  phase: Words | null;
  days: WeekDay[];
  later: LaterWeek[];
  /** When the week started (ISO), so the page can say which day it is. */
  startedAt: string;
}

/** The answers that pick a lower-back starting plan (the plan constitution's picking questions). */
export interface LowerBackPick {
  leg_pain: boolean;
  worse_with: "sitting_or_bending_forward" | "standing_or_bending_back" | "neither_or_unclear";
  buttock_or_pelvis: boolean;
  longer_than_12_weeks: boolean;
}

/** Where a guest's week 1 is kept on the phone. */
export const WEEK_ONE_KEY = "physiolife.weekOne";

/** The day of the week the patient is on (1 to 7), counted from the start; past day 7 it stays on 7. */
export function dayOf(w: Pick<WeekOne, "startedAt">, now = Date.now()): number {
  const start = new Date(w.startedAt);
  const days = Math.floor((new Date(now).setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0)) / 86_400_000);
  return Math.min(7, Math.max(1, days + 1));
}

/**
 * The week this phone keeps, through the library gate again (server/exercises/library-gate.ts): an exercise
 * Excluded since the week was saved leaves it, and a training day that had three main exercises and keeps
 * fewer is listed in `short`, so the page sends the patient to a physio instead of starting it.
 */
export function withoutExcluded(week: WeekOne, excluded: ReadonlySet<string>): { week: WeekOne; short: number[] } {
  if (!excluded.size) return { week, short: [] };
  const short: number[] = [];
  const mains = (d: WeekDay) => d.items.filter((i) => i.group === "main").length;
  const days = week.days.map((d) => {
    const items = d.items.filter((i) => !excluded.has(i.exercise.id));
    if (items.length === d.items.length) return d;
    const kept = { ...d, items, rest: d.rest || !items.length };
    if (!d.rest && mains(d) >= 3 && mains(kept) < 3) short.push(d.day);
    return kept;
  });
  return { week: { ...week, days }, short };
}
